import React, { useState } from "react";
import { Modal, DatePicker, TimePicker, Select, Button, App } from "antd";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import theme from "../themes";
import { getTimezoneOptions, TIMEZONE_CONFIG } from "../utils/timezoneUtils";
import ToastMessage from "../utils/toastMessage";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

// Extend dayjs with timezone plugins
dayjs.extend(utc);
dayjs.extend(timezone);

interface ScheduleCampaignModalProps {
  open: boolean;
  onCancel: () => void;
  onSchedule: (utcStartAt: string, tz: string) => Promise<void> | void;
}

const ScheduleCampaignModal: React.FC<ScheduleCampaignModalProps> = ({
  open,
  onCancel,
  onSchedule,
}) => {
  const { t } = useTranslation();
  const [scheduleDate, setScheduleDate] = useState<any>(null);
  const [scheduleTime, setScheduleTime] = useState<any>(null);
  const [scheduleAMPM, setScheduleAMPM] = useState<string>("AM");
  const [scheduleTZ, setScheduleTZ] = useState<string>("EST");
  const [submitting, setSubmitting] = useState(false);
  const { message } = App.useApp();
  const toast = ToastMessage(message);

  const handleSubmit = async () => {
    if (!scheduleDate || !scheduleTime) {
      toast.error(
        t("campaignModal.schedule.validation.scheduleDateAndTimeRequired")
      );
      return;
    }

    const utcStartAt = (() => {
      const dateStr = scheduleDate.format("YYYY-MM-DD");
      const timeStr = scheduleTime.format("hh:mm");
      const ampm = scheduleAMPM;

      // Convert 12-hour to 24-hour format
      let [hours, minutes] = timeStr.split(":");
      let hour = parseInt(hours);

      if (ampm === "PM" && hour !== 12) {
        hour += 12;
      } else if (ampm === "AM" && hour === 12) {
        hour = 0;
      }

      const formattedHour = hour.toString().padStart(2, "0");
      return `${dateStr}T${formattedHour}:${minutes}:00`;
    })();

    if (!utcStartAt) return;

    try {
      setSubmitting(true);
      await onSchedule(utcStartAt, scheduleTZ);
      // Reset local state on success
      setScheduleDate(null);
      setScheduleTime(null);
      setScheduleAMPM("AM");
      setScheduleTZ("EST");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      width={700}
      title={t("campaignModal.schedule.title")}
      footer={[
        <CancelButton key="cancel" onClick={onCancel}>
          {t("campaignModal.common.cancel")}
        </CancelButton>,
        <SubmitButton
          key="schedule"
          type="primary"
          loading={submitting}
          onClick={handleSubmit}
        >
          {t("campaignModal.schedule.scheduleButton")}
        </SubmitButton>,
      ]}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginTop: 24,
        }}
      >
        <div style={{ fontWeight: 500, width: 120 }}>
          {t("campaignModal.common.schedule")}
        </div>
        <Select
          value={scheduleTZ}
          onChange={(value) => {
            setScheduleTZ(value);
            setScheduleDate(null); // Clear date picker when timezone changes
          }}
          style={{ width: 120 }}
          size="large"
        >
          {getTimezoneOptions().map((tz) => (
            <Select.Option key={tz.value} value={tz.value}>
              {tz.label}
            </Select.Option>
          ))}
        </Select>
        <DatePicker
          placeholder={t("campaignModal.common.scheduleDatePlaceholder")}
          size="large"
          value={scheduleDate}
          onChange={setScheduleDate}
          style={{ width: 130 }}
          format="MM/DD/YYYY"
          disabledDate={(current) => {
            if (!current) return false;

            // Normalize current to start of day (remove time component)
            const currentDate = current.startOf("day");

            // Get today's date in the selected timezone
            const timezoneName =
              TIMEZONE_CONFIG[scheduleTZ as keyof typeof TIMEZONE_CONFIG];
            const todayInTimezone = timezoneName
              ? dayjs().tz(timezoneName).startOf("day")
              : dayjs().startOf("day");

            // Compare normalized calendar date with today in selected timezone
            const calendarDateStr = currentDate.format("YYYY-MM-DD");
            const todayStr = todayInTimezone.format("YYYY-MM-DD");

            return calendarDateStr < todayStr;
          }}
        />
        <TimePicker
          popupClassName="hide-ampm"
          size="large"
          placeholder={t("campaignModal.common.scheduleTimePlaceholder")}
          format="HH:mm"
          use12Hours
          value={scheduleTime}
          onChange={setScheduleTime}
          style={{ width: 130 }}
        />
        <Select
          value={scheduleAMPM}
          onChange={setScheduleAMPM}
          style={{ width: 70 }}
          size="large"
        >
          <Select.Option value="AM">AM</Select.Option>
          <Select.Option value="PM">PM</Select.Option>
        </Select>
      </div>
    </Modal>
  );
};

const SubmitButton = styled(Button)`
  background: ${theme.colors.green1};
  color: #fff;
  border: none;
  border-radius: 8px;
  height: 40px;
  padding: 0 16px;
  font-size: 16px;
  font-weight: 400;
  cursor: pointer;
  transition: background 0.2s;
  &:hover {
    background: #047857 !important;
  }
`;

const CancelButton = styled(Button)`
  background: ${theme.colors.grey3};
  color: #000;
  border: 1px solid ${theme.colors.grey2};
  border-radius: 8px;
  height: 40px;

  &:hover {
    border: 1px solid ${theme.colors.grey2} !important;
    background: ${theme.colors.grey3} !important;
    color: #000 !important;
  }
`;

export default ScheduleCampaignModal;
